import React, { useState } from 'react';
import { Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import './PasswordEdit.scss';
import DrawerFormContainer from 'components/drawerFormContainer/DrawerFormContainer';
import PasswordCreationForm, { PasswordCreationFormRef } from 'components/passwordCreationForm/PasswordCreationForm';

interface PasswordEditProps {
  visible: boolean;
  onClose: () => void;
  onSave?: (data: PasswordEditFormData) => Promise<void>;
}

export interface PasswordEditFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  logoutAllDevices: boolean;
}

const PasswordEdit: React.FC<PasswordEditProps> = ({
  visible,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation('profile');
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const passwordCreationFormRef = React.useRef<PasswordCreationFormRef>(null);

  const handleValuesChange = (changedValues: any) => {
    const fieldNames = Object.keys(changedValues);
    if (fieldNames.length > 0) {
      const fieldsToUpdate = fieldNames.map(name => ({
        name: name as any,
        errors: [],
      }));
      form.setFields(fieldsToUpdate);
    }
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      await onSave?.(values);

    } catch (error) {
      console.error('Validation or save failed:', error);

    }
  };

  const handleClose = () => {
    // Prevent closing the drawer while submitting
    if (isSubmitting) return;
    onClose();
    resetForm();
  };

  const resetForm = () => {
    form.resetFields();
    passwordCreationFormRef.current?.resetValidation();
  };

  return (
    <DrawerFormContainer
      title={t('passwordEdit.title')}
      description={t('passwordEdit.description')}
      visible={visible}
      onClose={handleClose}
      onSave={handleSave}
      saveButtonTitle={t('passwordEdit.savePassword')}
      isSubmitting={isSubmitting}
    >
      <Form
        form={form}
        layout="vertical"
        className="password-edit-form"
        initialValues={{ logoutAllDevices: true }}
        onValuesChange={handleValuesChange}
      >
        {/* Current Password Field */}
        <Form.Item
          name="currentPassword"
          label={t('passwordEdit.currentPassword')}
          rules={[
            {
              required: true,
              message: t('passwordEdit.validation.currentPasswordRequired'),
            },
          ]}
        >
          <Input.Password
            placeholder={t('passwordEdit.currentPassword')}
            iconRender={(visible) =>
              visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
            className="password-input"
            disabled={isSubmitting}//Disable on submit
          />
        </Form.Item>
        {/* Pass the disabled prop to PasswordCreationForm */}
        <PasswordCreationForm
          ref={passwordCreationFormRef}
          disabled={isSubmitting}
        />
      </Form>
    </DrawerFormContainer>
  );
};

export default PasswordEdit;